import React, { useRef, useState } from 'react';
import { Upload, Loader2, X, Images } from 'lucide-react';
import GalleryPicker from './GalleryPicker';

// Reads a file, draws it onto a 64x64 canvas (converting any format to PNG),
// returns a data URL. This normalises size and format for a browser-tab favicon.
function fileToFaviconDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        const size = 64;
        const canvas = document.createElement('canvas');
        canvas.width = size; canvas.height = size;
        const ctx = canvas.getContext('2d');
        // Fit the image into the square while preserving aspect ratio (contain).
        const scale = Math.min(size / img.width, size / img.height);
        const w = img.width * scale, h = img.height * scale;
        ctx.clearRect(0, 0, size, size);
        ctx.drawImage(img, (size - w) / 2, (size - h) / 2, w, h);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// props: value (url), onChange(url), upload(dataUrl, contentType) => Promise<url>
export function FaviconUpload({ value, onChange, upload, testid = 'favicon-upload' }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);

  const pick = () => inputRef.current && inputRef.current.click();

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setErr(''); setBusy(true);
    try {
      const dataUrl = await fileToFaviconDataUrl(file);
      const url = await upload(dataUrl, 'image/png');
      onChange(url);
    } catch (e2) {
      setErr('Could not process that image. Try a PNG, JPG or SVG.');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-3">
        {value ? (
          <img src={value} alt="favicon preview" className="w-8 h-8 rounded border" style={{ borderColor: 'rgba(0,0,0,0.15)' }} data-testid={`${testid}-preview`} />
        ) : (
          <div className="w-8 h-8 rounded border flex items-center justify-center text-[10px]" style={{ borderColor: 'rgba(0,0,0,0.15)', color: 'var(--olive)' }}>none</div>
        )}
        <button type="button" onClick={pick} disabled={busy} data-testid={`${testid}-btn`}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm font-title" style={{ background: 'var(--sage)', color: 'var(--ink)', opacity: busy ? 0.7 : 1 }}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {value ? 'Replace' : 'Upload'}
        </button>
        <button type="button" onClick={() => setPickerOpen(true)} data-testid={`${testid}-gallery`}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm font-title" style={{ background: 'var(--sage)', color: 'var(--ink)' }}>
          <Images size={14} /> Gallery
        </button>
        {value && (
          <button type="button" onClick={() => onChange('')} data-testid={`${testid}-clear`}
            className="inline-flex items-center gap-1 px-2 py-1.5 rounded text-sm" style={{ color: '#8a1f11' }}>
            <X size={14} /> Remove
          </button>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml,image/x-icon" onChange={onFile} className="hidden" data-testid={`${testid}-input`} />
      {err && <p className="text-xs mt-1" style={{ color: '#8a1f11' }}>{err}</p>}
      <GalleryPicker open={pickerOpen} onClose={() => setPickerOpen(false)} onSelect={(url) => onChange(url)} title="Choose a favicon" />
    </div>
  );
}
